import { useTranslation } from '../../shared/i18n';
import React, { useState, useEffect } from 'react';
import {
    Card, Button, List, Tag, Space, message, Modal, Empty, Spin, Popconfirm, Typography
} from 'antd';
import {
    PlusOutlined, CreditCardOutlined, BankOutlined, DeleteOutlined, StarOutlined, StarFilled
} from '@ant-design/icons';
import { loadStripe } from '@stripe/stripe-js/pure';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { paymentService } from '../services/paymentService';
import { PaymentErrorAlert } from './StripePaymentElementForm';
import { stripeErrorMessage } from '../utils/stripeErrors';

const { Text } = Typography;

// Card brand icons/colors
const cardBrandConfig = {
    visa: { color: '#1a1f71', name: 'Visa' },
    mastercard: { color: '#eb001b', name: 'Mastercard' },
    amex: { color: '#006fcf', name: 'American Express' },
    discover: { color: '#ff6000', name: 'Discover' },
    jcb: { color: '#0b4ea2', name: 'JCB' },
    unionpay: { color: '#e21836', name: 'UnionPay' },
    diners: { color: '#004c97', name: 'Diners Club' },
    default: { color: '#666', name: '银行卡' }
};

// Add a payment method with the Stripe Payment Element (SetupIntent): card, US bank account (ACH), Link,
// Cash App Pay, Amazon Pay — whatever the Stripe Dashboard enables and can be charged automatically later.
const AddMethodForm = ({ onSuccess, onCancel }) => {
    const { t } = useTranslation();
    const stripe = useStripe();
    const elements = useElements();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements) return;
        setLoading(true);
        setError(null);
        try {
            const { setupIntent, error: stripeError } = await stripe.confirmSetup({
                elements,
                confirmParams: { return_url: window.location.href.split('?')[0] },
                redirect: 'if_required',
            });
            if (stripeError) {
                if (stripeError.type !== 'validation_error') {
                    console.warn('confirmSetup error', stripeError);
                    setError(stripeErrorMessage(stripeError, t));
                }
                return;
            }
            const r = await paymentService.savePaymentMethod({ setupIntentId: setupIntent.id });
            if (r.success && r.data?.status === 'pending') {
                message.info(r.data.message || t('We will add this payment method once your bank account is verified'));
                onSuccess();
            } else if (r.success) {
                message.success(t('Payment method added'));
                onSuccess();
            } else {
                setError(stripeErrorMessage(r, t));
            }
        } catch (err) {
            console.warn('Add payment method failed', err);
            setError(stripeErrorMessage(err, t));
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <PaymentElement options={{ layout: { type: 'accordion', defaultCollapsed: false, radios: true, spacedAccordionItems: false } }}
                onChange={() => setError(null)} />
            <PaymentErrorAlert error={error} style={{ marginTop: 16 }} />
            <div style={{ textAlign: 'right', marginTop: 16 }}>
                <Space>
                    <Button onClick={onCancel} disabled={loading}>{t('Cancel')}</Button>
                    <Button type="primary" htmlType="submit" loading={loading} disabled={!stripe}>{t('Save')}</Button>
                </Space>
            </div>
        </form>
    );
};

const PaymentMethods = () => {
    const { t } = useTranslation();
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [stripePromise, setStripePromise] = useState(null);
    const [clientSecret, setClientSecret] = useState(null);
    const [setupLoading, setSetupLoading] = useState(false);

    useEffect(() => {
        loadPaymentMethods();
        loadStripeConfig();
        // Back from a redirect-based setup (e.g. bank login): save it, then clean the URL
        const params = new URLSearchParams(window.location.search);
        const si = params.get('setup_intent');
        if (si) {
            paymentService.savePaymentMethod({ setupIntentId: si })
                .then((r) => {
                    if (r.success && r.data?.status === 'pending') message.info(r.data.message);
                    else if (r.success) message.success(t('Payment method added'));
                    loadPaymentMethods();
                })
                .catch((e) => console.warn('save after redirect failed', e))
                .finally(() => {
                    ['setup_intent', 'setup_intent_client_secret', 'redirect_status'].forEach((k) => params.delete(k));
                    const q = params.toString();
                    window.history.replaceState(null, '', window.location.pathname + (q ? `?${q}` : ''));
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadStripeConfig = async () => {
        try {
            const response = await paymentService.getStripeConfig();
            if (response.success && response.data?.publishable_key) {
                setStripePromise(loadStripe(response.data.publishable_key));
            }
        } catch (error) {
            console.error('Failed to load Stripe config:', error);
        }
    };

    const loadPaymentMethods = async () => {
        setLoading(true);
        try {
            const response = await paymentService.getSavedMethods();
            if (response.success) {
                setPaymentMethods(response.data || []);
            } else {
                message.error(response.error || t('Failed to load payment methods'));
            }
        } catch (error) {
            message.error(t('Failed to load payment methods'));
        } finally {
            setLoading(false);
        }
    };

    const handleAddCard = async () => {
        setSetupLoading(true);
        try {
            const response = await paymentService.createSetupIntent();
            if (response.success && response.data?.client_secret) {
                setClientSecret(response.data.client_secret);
                setModalVisible(true);
            } else {
                message.error(response.error || t('Failed to initialize card linking'));
            }
        } catch (error) {
            if (!error?.toasted) message.error(t('Failed to initialize card linking'));
        } finally {
            setSetupLoading(false);
        }
    };

    const handleSetDefault = async (methodId) => {
        try {
            const response = await paymentService.setDefaultMethod(methodId);
            if (response.success) {
                message.success(t('Default payment method updated'));
                loadPaymentMethods();
            } else {
                message.error(response.error || t('Failed to set as default'));
            }
        } catch (error) {
            message.error(t('Failed to set default payment method'));
        }
    };

    const handleDelete = async (methodId) => {
        try {
            const response = await paymentService.deleteMethod(methodId);
            if (response.success) {
                message.success(t('Payment method removed'));
                loadPaymentMethods();
            } else {
                message.error(response.error || t('Failed to remove payment method'));
            }
        } catch (error) {
            message.error(t('Failed to remove payment method'));
        }
    };

    const handleModalClose = () => {
        setModalVisible(false);
        setClientSecret(null);
    };

    const handleAddSuccess = () => {
        handleModalClose();
        loadPaymentMethods();
    };

    const getCardBrand = (brand) => {
        const config = cardBrandConfig[brand?.toLowerCase()] || cardBrandConfig.default;
        return config;
    };

    const renderMethod = (method) => {
        const isCard = method.type === 'card';
        const brandConfig = getCardBrand(method.card_brand);
        const label = method.label || method.display_name
            || (isCard ? `${t(brandConfig.name)} •••• ${method.card_last4}` : method.type);
        return (
            <List.Item
                actions={[
                    method.is_default ? (
                        <Tag color="gold" icon={<StarFilled />}>{t('Default')}</Tag>
                    ) : (
                        <Button type="text" icon={<StarOutlined />} onClick={() => handleSetDefault(method.id)}>
                            {t('Set as default')}
                        </Button>
                    ),
                    <Popconfirm
                        title={t('Remove this payment method?')}
                        onConfirm={() => handleDelete(method.id)}
                        okText={t('Remove')}
                        cancelText={t('Cancel')}
                    >
                        <Button type="text" danger icon={<DeleteOutlined />}>{t('Remove')}</Button>
                    </Popconfirm>
                ]}
            >
                <List.Item.Meta
                    avatar={
                        <div style={{ width: 48, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5', borderRadius: 4 }}>
                            {method.type === 'us_bank_account'
                                ? <BankOutlined style={{ fontSize: 22, color: '#555' }} />
                                : <CreditCardOutlined style={{ fontSize: 24, color: brandConfig.color }} />}
                        </div>
                    }
                    title={<Text strong>{label}</Text>}
                    description={isCard && method.card_exp_month ? (
                        <Text type="secondary">
                            {t('Expires {{month}}/{{year}}', { month: method.card_exp_month?.toString().padStart(2, '0'), year: method.card_exp_year })}
                        </Text>
                    ) : null}
                />
            </List.Item>
        );
    };

    // 信用卡放最上面(自动续费余额不够时扣默认卡);其他方式(美国银行账户 / Link 等)放下面
    const cards = paymentMethods.filter((m) => m.type === 'card');
    const others = paymentMethods.filter((m) => m.type !== 'card');

    return (
        <Card
            title={t('Credit cards')}
            extra={
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAddCard}
                    loading={setupLoading}
                >
                    {t('Add card')}
                </Button>
            }
        >
            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                {t('Auto-renewal uses your balance first, then your default card')}
            </Text>
            <Spin spinning={loading}>
                {cards.length === 0 ? (
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={t("No saved payment methods yet")}
                    >
                        <Button type="primary" onClick={handleAddCard} loading={setupLoading}>
                            {t('Add card')}
                        </Button>
                    </Empty>
                ) : (
                    <List dataSource={cards} renderItem={renderMethod} />
                )}
                {others.length > 0 && (
                    <>
                        <Typography.Title level={5} style={{ marginTop: 16 }}>{t('Other payment methods')}</Typography.Title>
                        <List dataSource={others} renderItem={renderMethod} />
                    </>
                )}
            </Spin>

            <Modal
                title={t('Add payment method')}
                open={modalVisible}
                onCancel={handleModalClose}
                footer={null}
                destroyOnClose
            >
                {stripePromise && clientSecret ? (
                    <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'stripe' } }}>
                        <AddMethodForm onSuccess={handleAddSuccess} onCancel={handleModalClose} />
                    </Elements>
                ) : (
                    <Spin tip={t("Loading…")} />
                )}
            </Modal>
        </Card>
    );
};

export default PaymentMethods;
